import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DealPipeline, DealStageDef } from "@sales-ops-amplify/shared";
import {
  createDealPipeline,
  deleteDealPipeline,
  listDealPipelines,
  updateDealPipeline,
} from "@/api/deal-pipelines";

function emptyStage(order: number): DealStageDef {
  return {
    id: `stage_${Date.now()}_${order}`,
    name: "New stage",
    order,
    type: "open",
    probability: 50,
    forecastCategory: "pipeline",
  };
}

export function SettingsDealStagesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["deal-pipelines"],
    queryFn: listDealPipelines,
  });

  const pipelines = data?.pipelines ?? [];
  const [selectedId, setSelectedId] = useState<string>("");
  const [name, setName] = useState("");
  const [stages, setStages] = useState<DealStageDef[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [newPipelineName, setNewPipelineName] = useState("");

  const selected =
    pipelines.find((p) => p.pipelineConfigId === selectedId) ?? pipelines[0];

  useEffect(() => {
    if (!selected) return;
    setSelectedId(selected.pipelineConfigId);
    setName(selected.name);
    setStages(selected.stages);
  }, [selected?.pipelineConfigId, selected?.updatedAt]);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["deal-pipelines"] });

  const saveMutation = useMutation({
    mutationFn: () =>
      updateDealPipeline(selected!.pipelineConfigId, {
        name,
        stages: stages.map((s, i) => ({ ...s, order: i })),
      }),
    onSuccess: () => {
      void invalidate();
      setMessage("Pipeline saved.");
    },
    onError: (err: unknown) => {
      setMessage(err instanceof Error ? err.message : "Save failed");
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: () =>
      updateDealPipeline(selected!.pipelineConfigId, { isDefault: true }),
    onSuccess: () => {
      void invalidate();
      setMessage("Default pipeline updated.");
    },
    onError: (err: unknown) => {
      setMessage(err instanceof Error ? err.message : "Update failed");
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createDealPipeline({ name: newPipelineName.trim() || "New pipeline" }),
    onSuccess: (res) => {
      setNewPipelineName("");
      void invalidate();
      setSelectedId(res.pipeline.pipelineConfigId);
      setMessage("Pipeline created.");
    },
    onError: (err: unknown) => {
      setMessage(err instanceof Error ? err.message : "Create failed");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDealPipeline(selected!.pipelineConfigId),
    onSuccess: () => {
      void invalidate();
      setSelectedId("");
      setMessage("Pipeline deleted. Referencing deals moved to default.");
    },
    onError: (err: unknown) => {
      setMessage(err instanceof Error ? err.message : "Delete failed");
    },
  });

  const updateStage = (index: number, patch: Partial<DealStageDef>) => {
    setStages((prev) =>
      prev.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  const addStage = () => {
    setStages((prev) => [...prev, emptyStage(prev.length)]);
  };

  const selectPipeline = (p: DealPipeline) => {
    setSelectedId(p.pipelineConfigId);
    setName(p.name);
    setStages(p.stages);
    setMessage(null);
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <>
      <header className="mb-8 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Deal fields & stages
          </h1>
          <p className="text-muted-foreground">
            Used by Deals board and deal profiles. Manage multiple pipelines
            like Apollo Deal fields & stages.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected && !selected.isDefault ? (
            <button
              type="button"
              disabled={setDefaultMutation.isPending}
              onClick={() => setDefaultMutation.mutate()}
              className="rounded-md border border-border px-3 py-1.5 text-sm"
            >
              Set as default
            </button>
          ) : null}
          {selected && !selected.isDefault ? (
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (
                  confirm(
                    "Delete this pipeline? Deals using it will move to the default pipeline.",
                  )
                ) {
                  deleteMutation.mutate();
                }
              }}
              className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600"
            >
              Delete
            </button>
          ) : null}
          <button
            type="button"
            disabled={saveMutation.isPending || !selected}
            onClick={() => saveMutation.mutate()}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </header>

      {message ? (
        <p className="mb-4 text-sm text-muted-foreground">{message}</p>
      ) : null}

      <div className="mb-6 grid gap-6 lg:grid-cols-[220px_1fr]">
        <aside className="space-y-2">
          <p className="text-xs font-medium uppercase text-muted-foreground">
            Pipelines
          </p>
          <ul className="space-y-1">
            {pipelines.map((p) => (
              <li key={p.pipelineConfigId}>
                <button
                  type="button"
                  onClick={() => selectPipeline(p)}
                  className={`w-full rounded-md px-2 py-1.5 text-left text-sm ${
                    p.pipelineConfigId === selected?.pipelineConfigId
                      ? "bg-brand/10 font-medium text-brand"
                      : "hover:bg-muted/30"
                  }`}
                >
                  {p.name}
                  {p.isDefault ? (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (default)
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-border pt-3">
            <input
              className="mb-2 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
              placeholder="New pipeline name"
              value={newPipelineName}
              onChange={(e) => setNewPipelineName(e.target.value)}
            />
            <button
              type="button"
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="text-sm text-brand hover:underline"
            >
              + Create pipeline
            </button>
          </div>
        </aside>

        <div>
          {!selected ? (
            <p className="text-sm text-muted-foreground">No pipelines yet.</p>
          ) : (
            <>
              <label className="mb-4 block max-w-md text-sm">
                Pipeline name
                <input
                  className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>

              <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                        Order
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                        Stage
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                        Type
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                        Probability
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                        Forecast
                      </th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((stage, index) => (
                      <tr
                        key={stage.id}
                        className="border-b border-border last:border-0"
                      >
                        <td className="px-3 py-2 text-muted-foreground">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2">
                          <input
                            className="w-full rounded border border-border bg-background px-1.5 py-1"
                            value={stage.name}
                            onChange={(e) =>
                              updateStage(index, { name: e.target.value })
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            className="rounded border border-border bg-background px-1 py-1"
                            value={stage.type}
                            onChange={(e) =>
                              updateStage(index, {
                                type: e.target.value as DealStageDef["type"],
                              })
                            }
                          >
                            <option value="open">Open</option>
                            <option value="won">Won</option>
                            <option value="lost">Lost</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            className="w-20 rounded border border-border bg-background px-1.5 py-1"
                            value={stage.probability}
                            onChange={(e) =>
                              updateStage(index, {
                                probability: Number(e.target.value) || 0,
                              })
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            className="rounded border border-border bg-background px-1 py-1"
                            value={stage.forecastCategory}
                            onChange={(e) =>
                              updateStage(index, {
                                forecastCategory: e.target
                                  .value as DealStageDef["forecastCategory"],
                              })
                            }
                          >
                            <option value="pipeline">Pipeline</option>
                            <option value="best_case">Best case</option>
                            <option value="commit">Commit</option>
                            <option value="closed">Closed</option>
                            <option value="omit">Omit</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="text-xs text-red-600 hover:underline"
                            onClick={() =>
                              setStages((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={addStage}
                className="mt-3 text-sm text-brand hover:underline"
              >
                + Add stage
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
